/**
 * The HUB's copy of the 3D view's settings — how the house is lit and read.
 *
 * Owner, 2026-10-29: *"sun offset is not preserved on server"*, then *"all bim
 * settings should live on hub"*. Until now these lived in `localStorage` only
 * (one key per concern: `bim-sun-<hub key>`, `bim-nav-mode`, `bim-sim-view`,
 * `bim-room-labels:<id>`, `bim-view-visibility:<id>`), so a second device — or
 * the same device after clearing site data — opened the house at defaults. They
 * are the house's, so the hub keeps them under `bim_view_settings` and every
 * account renders the same view.
 *
 * `useBimScene` still keeps its own `localStorage` copy as a RENDER CACHE: the
 * first frame is painted from it before this endpoint has answered. This module
 * is the wire — the reading, and the paced write.
 */
import { apiFetch } from '@/services/api';

/** Exactly the hub's `BimViewSettings` (`services/bim/bimViewSettings.ts`). */
export interface HubBimViewSettings {
  version: number;
  timeOfDay: number;
  sunEnabled: boolean;
  exposure: number;
  ambientBoost: number;
  /** Degrees clockwise, 0…360 — the model's own north (the "sun offset"). */
  northAngle: number;
  latitude: number;
  /** `YYYY-MM-DD`. */
  solarDate: string;
  sunClockSynced: boolean;
  autoLighting: boolean;
  weatherSkyEnabled: boolean;
  lightPreset: 'low' | 'mid' | 'high';
  glowScale: number;
  showSunRay: boolean;
  /** The compass overlay on the canvas. */
  compass: boolean;
  /** The turntable, the walk, or the simulation. */
  navMode: 'orbit' | 'gravity' | 'sim';
  /** Inside `navMode: 'sim'` — the body's eye or the drone. */
  simView: 'pov' | 'drone';
  /** Room pills: an explicit mode, or `auto` (by navigation mode). */
  roomLabels: 'all' | 'current' | 'off' | 'auto';
  /** The view filter: which pin layers draw… */
  showRooms: boolean;
  showPeople: boolean;
  showDevices: boolean;
  /** …and what they narrow to (`''` = everything). */
  viewDeviceType: string;
  viewDeviceRoom: string;
  /** The see-through model, and how far it goes (percent). */
  transparencyEnabled: boolean;
  transparencyPercent: number;
  /**
   * The SECTION CUT the house was read with — a horizontal band, in the same shape
   * a preset stores. The hub's `BimViewSettings` grew it on 2026-10-30 so the band
   * is the HOUSE's, like the see-through: a reload opens cut the way it was left
   * and every surface reads the same level (owner: *"on dashboards ground view the
   * cut level is different than on bim page"*). Absent on rows written before it —
   * and absent means "unrecorded", never "off".
   */
  clipEnabled?: boolean;
  clipHeightY?: number | null;
  clipBottomY?: number | null;
  /** The sun's shadows on the 3D page. */
  shadows: boolean;
  /** The devices' own lights — fixture glow and their light pools. */
  lights: boolean;
  /** The room pages' own filter — shared by every room page. */
  roomView: HubBimRoomViewFilter;
}

/**
 * What a room page's embedded 3D view draws. One group for every room page: the
 * room is the page, so the filter is the operator's taste, not a per-room fact.
 */
export interface HubBimRoomViewFilter {
  /** People/avatars drawn in the room's view? */
  people: boolean;
  /** The other rooms' pills drawn (tap one to open that room)? */
  rooms: boolean;
  /** Device pins drawn at all? */
  devicesOn: boolean;
  /** This room's devices, or every device on the model. */
  devices: 'room' | 'all';
  /** The sun's shadows drawn in the room's view? */
  shadows: boolean;
  /** The devices' own lights — fixture glow and their pools. */
  lights: boolean;
  /** The sun itself (its daylight) — off reads the room by its own lamps. */
  sun: boolean;
  /** The live weather sky behind the house, instead of the plain gradient. */
  weather: boolean;
}

export interface StoredHubBimViewSettings {
  /** `null` when the hub has never been told — the local cache stands. */
  settings: HubBimViewSettings | null;
  updatedAt: string | null;
}

const VIEW_SETTINGS_PATH = '/bim-models/view-settings';

/**
 * The hub's copy, or `settings: null` when nothing has been saved yet. Throws
 * when the hub cannot be reached — the caller keeps the cached values.
 */
export async function loadHubBimViewSettings(): Promise<StoredHubBimViewSettings> {
  const answer = await apiFetch<Partial<StoredHubBimViewSettings>>(VIEW_SETTINGS_PATH);
  const settings = answer?.settings;
  if (!settings || typeof settings !== 'object') return { settings: null, updatedAt: answer?.updatedAt ?? null };
  return { settings: settings as HubBimViewSettings, updatedAt: answer.updatedAt ?? null };
}

/** Replace the hub's copy. The hub normalises it; this only sends the blob. */
export async function saveHubBimViewSettings(settings: HubBimViewSettings): Promise<void> {
  await apiFetch(VIEW_SETTINGS_PATH, { method: 'PUT', body: JSON.stringify(settings) });
}
